import React from "react";

import MenuIcon from "@uui/DotsHorizontal";
import { useParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import {
  FloatingNavActionDivider,
  FloatingNavIconButton,
} from "@app/analytics/components/reports/FloatingNav";
import useEditReport from "@app/analytics/MeetingReport/hooks/useEditReport";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import { toast } from "@app/common/hooks/useToasts";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import ConfirmationModal from "@rui/ConfirmationModal";
import InfoPanel from "@rui/InfoPanel";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import TooltipWrapper from "@rui/TooltipWrapper";

const RestoreMenu = () => {
  const [modal, setModal] = useEditStore((state) => [
    state.modal,
    state.setModal,
  ]);
  const { sessionId } = useParams() as { sessionId: string };
  const { open, onClose, onClick, anchorEl } = useAnchorElement();

  const {
    canRestoreNotes,
    canRestoreTranscript,
    restoreTranscript,
    restoreNotes,
  } = useEditReport();

  const closeModal = () => setModal(null);

  const handleClickRevertNotes = () => {
    setModal("confirm_revert_notes");
    onClose();
  };

  const handleRestoreNotes = () => {
    restoreNotes.mutate(sessionId, {
      onError: () => {
        toast.error(
          "Something went wrong and the notes could not be reverted. Please try again or reach out to support@read.ai if the problem persists."
        );
      },
    });
  };

  const handleClickRevertTranscript = () => {
    setModal("confirm_revert_transcript");
    onClose();
  };

  const handleRestoreTranscript = () => {
    restoreTranscript.mutate(sessionId, {
      onError: () => {
        toast.error(
          "Something went wrong and the transcript could not be reverted. Please try again or reach out to support@read.ai if the problem persists."
        );
      },
    });
  };

  return (
    <>
      <FloatingNavActionDivider />
      <FloatingNavIconButton Icon={MenuIcon} onClick={onClick} />
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <TooltipWrapper
          title="The notes have not been edited"
          show={!canRestoreNotes}
          enterDelay={2000}
        >
          <MenuItem
            disabled={!canRestoreNotes}
            onClick={handleClickRevertNotes}
          >
            Revert notes to original
          </MenuItem>
        </TooltipWrapper>
        <TooltipWrapper
          title="The transcript has not been edited"
          show={!canRestoreTranscript}
          enterDelay={2000}
        >
          <MenuItem
            disabled={!canRestoreTranscript}
            onClick={handleClickRevertTranscript}
          >
            Revert transcript to original
          </MenuItem>
        </TooltipWrapper>
      </Menu>

      <ConfirmationModal
        open={modal === "confirm_revert_transcript"}
        onClose={closeModal}
        onConfirm={handleRestoreTranscript}
        title="Revert all changes?"
        size="md"
        confirmProps={{
          label: "Yes, revert changes",
          type: "negative",
        }}
        loading={restoreTranscript.isPending}
      >
        <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
          Reverting the transcript to its original state will reverse any
          changes made, including updates to speaker names. Are you sure you
          want to continue?
        </Typography>
        <InfoPanel severity="info">
          Only report owners and editors can make and revert changes to the
          report.
        </InfoPanel>
      </ConfirmationModal>
      <ConfirmationModal
        open={modal === "confirm_revert_notes"}
        onClose={closeModal}
        onConfirm={handleRestoreNotes}
        title="Revert all changes?"
        size="md"
        confirmProps={{
          label: "Yes, revert changes",
          type: "negative",
        }}
        loading={restoreNotes.isPending}
      >
        <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
          When you revert the notes to their original state, you will reverse
          any changes made. Are you sure you want to continue?
        </Typography>
        <InfoPanel severity="info">
          Only report owners and editors can make and revert changes to the
          report.
        </InfoPanel>
      </ConfirmationModal>
    </>
  );
};

export default RestoreMenu;
